/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useRef, useCallback, useEffect, useState } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { containsKey, Keys, isKeyDefined } from '../utils/UNSAFE_keys';
import { Size } from '../utils/UNSAFE_size';
import {
  getPrevNextKeyByCount,
  getFirstVisibleKey,
  getViewportConfig,
  findElementByKey,
  keyExtractor,
  handleSelectionRange
} from '../utils/PRIVATE_collectionUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { useCurrentKey } from '../hooks/PRIVATE_useCurrentKey';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { useTabbableModeSet } from '../hooks/PRIVATE_useTabbableModeSet';
import { useSelection, SelectionRangeDetail } from '../hooks/PRIVATE_useSelection';
import { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import {
  ItemContext,
  ListItemContext,
  CurrentKeyDetail,
  SelectionDetail,
  SelectionMode
} from '../UNSAFE_Collection';
import { Grid } from '../UNSAFE_Grid';
import { Selector } from '../UNSAFE_Selector';
import { Skeleton } from '../UNSAFE_Skeleton';
import { SkeletonContainer } from '../PRIVATE_List/SkeletonContainer';
import { LoadMoreCollection, LOADMORE_STYLE_CLASS } from '../PRIVATE_LoadMoreCollection';
import { MemoizeCardGridItem, ITEM_SELECTOR } from './CardGridItem';
import { styles } from './themes/CardGridStyles.css';

/**
 * Props for the GradGrid Component, which consist of some props from ListView
 */
type CardGridProps<K, D> = {
  /**
   * An array of shaped data that provide information to this CardGrid.
   * If the value is null, then CardGrid will show loading indicator until the value is set.
   */
  data: D[] | null;
  /**
   * Callback function to handle when viewport has changed, including the case
   * where user scrolls to the end of the component and there are more items to load.
   */
  onLoadMore?: () => void;
  /**
   * A boolean that indicates whether there are still more data to load.
   */
  hasMore?: boolean;
  /**
   * A callback function to get the key for a given row
   */
  getRowKey: (data: D) => K;
  /**
   * A function to render each item
   */
  children: (context: ListItemContext<K, D>) => ComponentChildren;
  /**
   * The key of the item that currently have keyboard focus. Ignored if the current item is not
   * currently in the viewport.
   */
  currentKey?: K;
  /**
   * Callback function to handle when current focused item has changed.  The function should
   * update the currentKey prop with a new current key.
   */
  onCurrentKeyChange?: (detail: CurrentKeyDetail<K>) => void;
  /**
   * The type of selection behavior that is enabled on the CardGrid. This property controls the number
   * of selections that can be made via selection gestures at any given time.
   */
  selectionMode?: SelectionMode;
  /**
   * The keys of the current selected items in the CardGrid.
   */
  selectedKeys?: Keys<K>;
  /**
   * Callback function to handle when selection has changed.  The function should update
   * the selectedKeys prop with a new set of selected keys.
   */
  onSelectionChange?: (detail: SelectionDetail<K>) => void;
  /**
   * The ViewportConfig currently contains only one property which allows application to specify what the scroller element should be.
   * If not specified, then the root element of CardGrid itself will be the scroller element.
   */
  viewportConfig?: ViewportConfig;
  /**
   * An aria-label which defines a string value that labels this CardGrid.
   * Either aria-label or aria-labelledby should be specified in order to make CardGrid accessible.
   */
  'aria-label'?: string;
  /**
   * An aria-labelledby which identifies the element(s) that labels this CardGrid.
   * Either aria-label or aria-labelledby should be specified in order to make CardGrid accessible.
   */
  'aria-labelledby'?: string;
  /**
   * Specify animation when cards are initially rendered.
   */
  initialAnimation?: 'slideUp' | 'slideDown';
  /**
   * Size of the gutter between columns.
   */
  gutterSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
};

export function CardGrid<K extends string | number, D>({
  children,
  data,
  onLoadMore = () => {},
  hasMore = false,
  getRowKey,
  currentKey,
  onCurrentKeyChange,
  selectionMode = 'none',
  selectedKeys = emptyKeys as Keys<K>,
  onSelectionChange,
  viewportConfig,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  initialAnimation = 'slideUp',
  gutterSize = 'sm'
}: CardGridProps<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);

  // tracking the anchor key which is used for shift+click selection
  const anchorKey = useRef<K | undefined>();
  // tracking pending current key in pointer down event
  const pendingCurrentKey = useRef<K>();

  const isInitialAnimation = useRef(true);
  const [colCount, setColCount] = useState(0);
  const [cardDimension, setCardDimension] = useState({ width: 0, height: 0 });

  const gutterSizePX = gutterSizeMappings[gutterSize];
  const gap = `${gutterSizePX / 4}x` as Size;
  const gridTemplateColumns = cardDimension.width
    ? `repeat(auto-fit, ${cardDimension.width}px)`
    : undefined;
  const gridProps = { gap, gridTemplateColumns };

  const ariaMultiSelectable = selectionMode === 'none' ? undefined : selectionMode === 'multiple';
  const ariaRowcount = hasMore || !data || colCount === 0 ? -1 : Math.ceil(data?.length / colCount);

  /*****************************************************************************************/
  /********************************* cardWidth and colCount ********************************/
  /*****************************************************************************************/

  // cards are fully rendered for the first time, only when cardWidth and colCount have defined and non-zero values
  // for the first time, so the initial animation should not happen until this render.
  useEffect(() => {
    if (cardDimension.width && colCount) {
      isInitialAnimation.current = false;
    }
  }, [cardDimension.width && colCount]);

  const handleColCount = useCallback(
    (rootWidth: number) => {
      const newColCount = getColCount(
        gutterSizePX,
        cardDimension.width || DEFAULT_SKELETON_WIDTH,
        rootWidth
      );
      if (newColCount > 0) {
        setColCount(newColCount);
      }
    },
    [gutterSizePX, cardDimension.width]
  );

  useEffect(() => {
    const root = rootRef?.current;
    if (root) {
      handleColCount(root.offsetWidth);
    }
  }, [handleColCount]);

  const handleResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        // update colCount during resize
        handleColCount(entry.contentRect.width);
      }
    },
    [handleColCount]
  );

  /**
   * Set up resize observer and update the colCount, to ensure keyboard navigation is correct
   * after the resize
   */
  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        handleResize(entries);
      });
      resizeObserver.observe(root);
      return () => {
        resizeObserver.unobserve(root);
      };
    }
    return undefined;
  }, [handleResize]);

  /*****************************************************************************************/
  /************************************* Tabbable Mode *************************************/
  /*****************************************************************************************/

  const cardGridContains = (elem: Element | null) => {
    return !!rootRef.current?.contains(elem);
  };

  let pendingFocusRingChange = false;
  const [isCurrentTabbableKey, tabbableModeProps] = useTabbableModeSet(
    cardGridContains,
    (element) => {
      return keyExtractor(element, ITEM_SELECTOR) as K;
    },
    currentKey,
    // handle clean-up on disabling of the mode
    (target: EventTarget | null, isKeyboardEvent) => {
      if (cardGridContains(target as Element)) {
        // delay focus reset for keyboard disable so focus is reset to CardGrid after the FocusTrap blur lifecycle completes.
        // pointer initiated changes will fire the onBlur organically and delaying the focus, just generates an unwanted second blur
        if (isKeyboardEvent) {
          // do not show a focus ring during this timeOut
          pendingFocusRingChange = true;
          setTimeout(() => {
            rootRef.current?.focus();
          }, 0);
        } else {
          rootRef.current?.focus();
        }
      }
      // otherwise target is not in list (including body, which target should be null in that case)
    },
    // set currentKey when mode is enabled
    (key: K) => {
      onCurrentKeyChange && onCurrentKeyChange({ value: key });
    }
  );

  /*****************************************************************************************/
  /********************************* Current Key and Focus *********************************/
  /*****************************************************************************************/

  const { currentKeyProps } = useCurrentKey(
    (element) => keyExtractor(element, ITEM_SELECTOR),
    selectionMode !== 'multiple',
    // init a function every time to make sure we pass in the latest colCount.current,
    // otherwise the new key might be wrong after resize
    () => getPrevNextKeyByCount(data, getRowKey, currentKey, -colCount),
    () => getPrevNextKeyByCount(data, getRowKey, currentKey, colCount),
    () => getPrevNextKeyByCount(data, getRowKey, currentKey, -1),
    () => getPrevNextKeyByCount(data, getRowKey, currentKey, 1),
    currentKey,
    onCurrentKeyChange
  );

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(cardGridContains, [
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight'
  ]);

  const handleInitialFocus = () => {
    if (
      !isKeyDefined(currentKey) &&
      onCurrentKeyChange &&
      isCurrentTabbableKey(undefined) &&
      rootRef.current
    ) {
      const firstKey =
        pendingCurrentKey.current || (getFirstVisibleKey(rootRef.current, ITEM_SELECTOR) as K);
      if (isKeyDefined(firstKey)) {
        onCurrentKeyChange({ value: firstKey });
      }
    }
  };

  /**
   * Need to track pointer down element to set currentItem when initial focus happens
   */
  const handlePointerDown = (event: PointerEvent) => {
    const key = keyExtractor(event.target as HTMLElement, ITEM_SELECTOR);
    if (isKeyDefined(key)) {
      pendingCurrentKey.current = key as K;
    }
  };

  useEffect(() => {
    if (currentKey != null && rootRef.current) {
      const elem = findElementByKey(rootRef.current, currentKey, ITEM_SELECTOR);
      if (elem) {
        // make sure item is visible
        elem.scrollIntoView({ block: 'nearest' });
        const cell = elem.closest(`[role=gridcell]`);
        if (cell) {
          // update aria-activedescendant for screen reader
          rootRef.current.setAttribute('aria-activedescendant', cell.id);
        }
      }
    }
    // listen to data because the current item would re-render when data is updated, i.e. the id
    // of current item would be updated, so the currentKey itself couldn't guarantee the root has
    // the latest aria-activedescendant
  }, [currentKey, data]);

  /*****************************************************************************************/
  /*************************************** Selection ***************************************/
  /*****************************************************************************************/

  const handleSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      if (onSelectionChange) {
        if (detail.value.all === false && detail.value.keys.size > 0) {
          // the last key selected by a user gesture is the anchor key
          anchorKey.current = Array.from(detail.value.keys.values()).pop();
        }
        onSelectionChange(detail);
      }
    },
    [anchorKey, onSelectionChange]
  );

  const handleSelectionRangeChange = useCallback(
    (detail: SelectionRangeDetail<K>) => {
      if (data && onSelectionChange) {
        const value = handleSelectionRange(detail, data, getRowKey);
        // for non-virtualized CardGrid, value should always be an array
        if (Array.isArray(value)) {
          onSelectionChange({ value: { all: false, keys: new Set(value) }, target: null });
        }
      }
    },
    [data, onSelectionChange, onLoadMore]
  );

  const { selectionProps } = useSelection(
    (element: HTMLElement) => {
      if (element === rootRef.current) {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, ITEM_SELECTOR) as K;
    },
    selectedKeys,
    selectionMode,
    false,
    'replace',
    false,
    handleSelectionChange,
    anchorKey.current,
    currentKey,
    (currentKey: K | undefined, isPrev: boolean) => () =>
      getPrevNextKeyByCount(data, getRowKey, currentKey, isPrev ? -colCount : colCount),
    (currentKey: K | undefined, isPrev: boolean) => () =>
      getPrevNextKeyByCount(data, getRowKey, currentKey, isPrev ? -1 : 1),
    (key: K) => {
      if (rootRef.current) {
        const elem = findElementByKey(rootRef.current, key, ITEM_SELECTOR);
        if (elem) {
          elem.scrollIntoView({ block: 'nearest' });
        }
      }
    },
    handleSelectionRangeChange
  );

  /*****************************************************************************************/
  /************************************ Child Component ************************************/
  /*****************************************************************************************/

  const getItemContext = useCallback(
    (context: ItemContext<D>) => {
      const key = getRowKey(context.data);
      const selectorRenderer =
        selectionMode === 'multiple'
          ? () => (
              <Selector
                onChange={onSelectionChange}
                rowKey={key}
                selectedKeys={selectedKeys}></Selector>
            )
          : undefined;

      return {
        index: context.index,
        data: context.data,
        metadata: { key: key },
        selector: selectorRenderer,
        isSelected: containsKey(selectedKeys, key)
      };
    },
    [selectionMode, selectedKeys, onSelectionChange]
  );

  const childrenComponent = (context: ItemContext<D>) => {
    const cardItemContext = getItemContext(context);
    const isTabbable = isCurrentTabbableKey(cardItemContext.metadata.key);
    const isFocused = currentKey === cardItemContext.metadata.key && !isTabbable;
    const isFocusRingVisible = isFocused && showFocusRing && !pendingFocusRingChange;
    const isSelected = cardItemContext.isSelected;
    const cardGridFunc = (context: ListItemContext<K, D>) => (
      <MemoizeCardGridItem
        key={cardItemContext.metadata.key}
        itemKey={cardItemContext.metadata.key}
        itemIndex={cardItemContext.index}
        itemData={cardItemContext.data}
        isTabbable={isTabbable}
        isFocused={isFocused}
        isFocusRingVisible={isFocusRingVisible}
        isSelected={isSelected}
        selectionMode={selectionMode}
        {...(isInitialAnimation.current && { initialAnimation })}
        {...(context.index === 0 && { setCardDimension })}>
        {children(context)}
      </MemoizeCardGridItem>
    );
    return cardGridFunc(cardItemContext);
  };

  // todo: issue track in JET-57952, need a way to show the skeletons in keyboard navigation
  const skeletons = findDefaultLoadMoreIndicator(
    gridProps,
    data?.length || 0,
    colCount,
    cardDimension.height
  );

  viewportConfig = getViewportConfig(rootRef, viewportConfig);
  const collectionComponent = (
    <div role="row">
      <Grid {...gridProps}>
        <LoadMoreCollection
          data={data}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          loadMoreIndicator={skeletons}
          loadMoreThreshold={gutterSizePX + 4} // gap size + padding
          viewportConfig={viewportConfig}>
          {childrenComponent}
        </LoadMoreCollection>
      </Grid>
    </div>
  );

  return (
    <div
      {...mergeProps(tabbableModeProps, currentKeyProps, focusRingProps, selectionProps, {
        onFocus: handleInitialFocus,
        onPointerDown: handlePointerDown
      })}
      ref={rootRef}
      role="grid"
      class={styles.baseStyle}
      tabIndex={0}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-multiselectable={ariaMultiSelectable}
      aria-rowcount={ariaRowcount}
      aria-colcount={colCount}>
      {data == null ? defaultLoadingIndicator(gridProps) : collectionComponent}
    </div>
  );
}

const emptyKeys = { all: false, keys: new Set() };

/**
 * A mapping of gutterSize to concrete size that can be passed into Grid component
 * (specifically, in the format of `${number}x`)
 */
const gutterSizeMappings: Record<string, number> = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 48,
  xl: 64
};

// these are arbitrary values by now, might update them with definite values in the future
const DEFAULT_SKELETON_NUMBER = 25;
const DEFAULT_SKELETON_WIDTH = 260;
const DEFAULT_SKELETON_HEIGHT = 300;

/**
 * A function that returns an array of skeletons based on count
 * @param count the number of skeletons
 */
const getSkeletonsByCount = (count: number, isUnique?: boolean, height?: number) =>
  [...Array(count)].map(() => (
    // this map function keeps generating the same copy of skeletons with same keys even with useId() hook,
    // so combine id with time to make the key unique. create unique skeletons if needed, otherwise the
    // animations won't start at the same time
    <SkeletonContainer key={isUnique && useId() + new Date().getTime()} minimumCount={1}>
      {() => {
        return <Skeleton height={`calc(${height || DEFAULT_SKELETON_HEIGHT}px)` as Size} />;
      }}
    </SkeletonContainer>
  ));

/**
 * The default loading indicator will display 25 card skeletons
 * @param gridProps the Grid props
 */
const defaultLoadingIndicator = (gridProps: any) => (
  <Grid {...gridProps}>{getSkeletonsByCount(DEFAULT_SKELETON_NUMBER)}</Grid>
);

/**
 * A function that will return the load more skeletons based on the number of columns and items, and return
 * the corresponding number of columns that is used to layout the skeletons.
 * @param gridProps the Grid props
 * @param totalCount the number of all items
 * @param colCount the number of columns in a row
 */
const findDefaultLoadMoreIndicator = (
  gridProps: any,
  totalCount: number,
  colCount: number,
  skeletonHeight: number
): ComponentChildren => {
  // if the number of columns is unknown, return nothing
  if (!colCount) {
    return undefined;
  }

  const skeletonGridProps = { ...gridProps };
  if (colCount && !totalCount) {
    skeletonGridProps.gridTemplateColumns = `repeat(auto-fit, ${DEFAULT_SKELETON_WIDTH}px)`;
  }

  const skeletons = [];
  const gridColumns: number[] = [];

  const firstCount = colCount - (totalCount % colCount);
  if (firstCount > 0) {
    const firstSkeletons = getSkeletonsByCount(firstCount, true, skeletonHeight);
    skeletons.push(firstSkeletons);
    gridColumns.push(firstCount);
  }
  const secondCount = colCount;
  if (firstCount < secondCount) {
    const secondSkeletons = getSkeletonsByCount(secondCount, true, skeletonHeight);
    skeletons.push(secondSkeletons);
    gridColumns.push(secondCount);
  }

  // only apply LOADMORE_STYLE_CLASS on the first row, to avoid onLoadMore
  // get called multiple times
  const skeletonRows = skeletons.map((s, i) => (
    <div
      {...(i == 0 && { class: LOADMORE_STYLE_CLASS })}
      style={{ gridColumn: `span ${gridColumns[i]}` }}>
      <Grid {...skeletonGridProps}>{s}</Grid>
    </div>
  ));
  return skeletonRows;
};

const getColCount = (gapWidth: number, cardWidth?: number, rootWidth?: number) => {
  if (!cardWidth || !rootWidth) return 0;

  // if we have n cards, we have n-1 gaps
  const colCount = Math.floor((rootWidth + gapWidth) / (gapWidth + cardWidth));
  return colCount;
};
